import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useToast } from '@/hooks/use-toast'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'

const SignUp = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [company, setCompany] = useState('')
  const [role, setRole] = useState('broker')
  const [isLoading, setIsLoading] = useState(false)
  const { signUp } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  
  usePageTitle("Sign Up")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    const { error } = await signUp(email, password, fullName, company, role)

    if (error) {
      // Check for rate limit error
      if (error.message.includes('Email rate limit exceeded') || error.message.includes('rate limit')) {
        toast({
          title: 'Too Many Requests',
          description: 'Please wait a few minutes before trying again. If this persists, contact support at info@dowdgroup.com.au',
          variant: 'destructive',
        })
      }
      // Check for duplicate email error
      else if (error.message.includes('already registered') || error.message.includes('already exists')) {
        toast({
          title: 'Email Already Registered',
          description: 'This email is already registered. Please use a different email or sign in instead.',
          variant: 'destructive',
        })
      } 
      // Generic error
      else {
        toast({
          title: 'Registration Error',
          description: error.message || 'An error occurred during registration. Please try again or contact support.',
          variant: 'destructive',
        })
      }
    } else {
      toast({
        title: 'Success',
        description: 'Account created successfully! Please check your email to verify your account.',
      })
      
      // Show success message and let user check their email
      // They will be redirected after email verification
    }

    setIsLoading(false)
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          <Card className="shadow-medium">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-primary">Create Your Account</CardTitle>
              <CardDescription>
                Join Australia's premier business marketplace - available on web and mobile app
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-3">
                  <Label>I want to register as:</Label>
                  <RadioGroup value={role} onValueChange={setRole}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="seller" id="seller" />
                      <Label htmlFor="seller" className="cursor-pointer">
                        <div>
                          <div className="font-medium">Business Seller</div>
                          <div className="text-sm text-muted-foreground">Sell your business with access to analytics and listings</div>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="broker" id="broker" />
                      <Label htmlFor="broker" className="cursor-pointer">
                        <div>
                          <div className="font-medium">Business Broker</div>
                          <div className="text-sm text-muted-foreground">Full broker access with advanced tools and client management</div>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="buyer" id="buyer" />
                      <Label htmlFor="buyer" className="cursor-pointer">
                        <div>
                          <div className="font-medium">Business Buyer</div>
                          <div className="text-sm text-muted-foreground">Browse businesses with smart matching and favorites</div>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
              <div className="mt-4 text-center text-sm">
                Already have an account?{' '}
                <Link to="/sign-in" className="text-primary hover:underline">
                  Sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default SignUp